import React from 'react';
import {
  Box, Typography, Chip, Stack,
  Avatar, Button, Tooltip, Divider, Popover,
} from '@mui/material';
import { Info as InfoIcon, Logout as LogoutIcon, AccountCircle as AccountCircleIcon } from '@mui/icons-material';
import { useAppSelector } from '../../store';
import { useCurrentUser, useLogout } from '../../auth/AuthProvider';

interface HeaderProps {
  title?: string;
}

const Header: React.FC<HeaderProps> = ({ title }) => {
  const hasFileChange = useAppSelector((state) => state.ui.hasFileChange);
  const user          = useCurrentUser();
  const logout        = useLogout();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const displayName = user?.username || 'User';
  const initials    = displayName.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase();
  const open        = Boolean(anchorEl);

  return (
    <Box sx={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      backgroundColor: 'background.paper', padding: '12px 20px',
      borderRadius: '12px', marginBottom: '14px',
      border: '1px solid', borderColor: 'divider',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Typography sx={{ fontSize: 28 }}>⚖️</Typography>
        <Typography variant="h1"
          sx={{ fontSize: '20px', fontWeight: 600, color: '#1a1a2e', lineHeight: 1.2 }}>
          {title || 'Demand Reapportion Agent'}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="center">
        {hasFileChange && (
          <Chip icon={<InfoIcon sx={{ fontSize: 14 }} />} label="File change" size="small"
            sx={{
              backgroundColor: '#fff3e0', color: '#e65100',
              border: '1px solid #ffcc80', fontSize: '11px',
              '& .MuiChip-icon': { color: '#e65100' },
            }}
          />
        )}

        <Tooltip title={displayName} placement="bottom">
          <Avatar onClick={(e) => setAnchorEl(e.currentTarget)} sx={{
            width: 34, height: 34, fontSize: '13px', fontWeight: 700,
            backgroundColor: '#ff9800', cursor: 'pointer',
            border: '2px solid', borderColor: open ? 'primary.main' : 'transparent',
            transition: 'border-color 0.15s',
            '&:hover': { borderColor: 'primary.main' },
          }}>
            {initials || <AccountCircleIcon fontSize="small" />}
          </Avatar>
        </Tooltip>

        <Popover open={open} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { mt: 1, borderRadius: 2, minWidth: 220, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', border: '1px solid', borderColor: 'divider' } }}
        >
          <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ width: 40, height: 40, fontSize: '14px', fontWeight: 700, backgroundColor: '#ff9800' }}>
                {initials || <AccountCircleIcon />}
              </Avatar>
              <Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1a1a2e' }}>{displayName}</Typography>
                <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>Signed in</Typography>
              </Box>
            </Stack>
          </Box>
          <Divider />
          <Box sx={{ p: 1 }}>
            <Button fullWidth size="small" startIcon={<LogoutIcon sx={{ fontSize: 16 }} />}
              onClick={logout}
              sx={{
                justifyContent: 'flex-start', textTransform: 'none',
                fontSize: '13px', color: 'text.primary',
                borderRadius: 1.5, px: 1.5, py: 0.75,
                '&:hover': { backgroundColor: '#fef2f2', color: '#d32f2f' },
              }}>
              Sign out
            </Button>
          </Box>
        </Popover>
      </Stack>
    </Box>
  );
};

export default Header;
